import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";
import type {IStandingsStore} from "data/stores/standings/standings.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {eq, findIndex, orderBy, take} from "lodash";
import {ConferenceType} from "data/enums";
import type {IConferencesStore} from "data/stores/conferences/conferences.store";
import type {IOrderedStandingsStore} from "data/stores/ordered_standings/ordered_standings.store";
import type {ILightPredictionsStore} from "data/stores/light_predictions/light_predictions.store";

interface IProps {
	squadId: number;
	position: number;
	conferenceType: ConferenceType;
}

export interface ILightPredictionSlotController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get squad(): ISquad | undefined;
	resultedClass: () => string;
}

@injectable()
export class LightPredictionSlotController implements ILightPredictionSlotController {
	@observable private _squadId: number = 0;
	@observable private _position: number = 0;
	@observable private _conferenceType: ConferenceType = ConferenceType.AFC;

	get squad() {
		return this._squadStore.getSquadById(this._squadId);
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.ConferencesStore) private _conferenceStore: IConferencesStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore,
		@inject(Bindings.OrderedStandingsStore) private _orderStandingsStore: IOrderedStandingsStore
	) {
		makeAutoObservable(this);
	}

	resultedClass = () => {
		const conferenceId = this._conferenceStore.getConferenceIdByType(this._conferenceType);
		// const isAFC = this._conferenceType === ConferenceType.AFC;

		const defaultStandings = this._standingsStore.standings.filter((it) => {
			if (it.roundId === this._roundsStore.actualStandingsRoundId) {
				const squad = this._squadStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, conferenceId);
			}
			return false;
		});

		const predictions = [...this._lpStore.afcPredictions, ...this._lpStore.nfcPredictions];
		const squadPrediction = predictions.find((it) => it.squadId === this._squadId);

		const orderedDefaultStandings = take(
			// this._orderStandingsStore.getSortedStandings(defaultStandings, conferenceId),
			orderBy(defaultStandings, ["conferenceRank"], ["asc"]),
			7
		);

		if (
			squadPrediction?.position ===
			findIndex(orderedDefaultStandings, (it) => it.squadId === this._squadId) + 1
		) {
			return "perfect_hit";
		}

		if (orderedDefaultStandings.map((it) => it.squadId).includes(this._squadId)) {
			return "hit";
		}

		if (!orderedDefaultStandings.map((it) => it.squadId).includes(this._squadId)) {
			return "no_hit";
		}

		return "";
	};

	dispose() {
		return;
	}

	init({squadId, position, conferenceType}: IProps) {
		this._squadId = squadId;
		this._position = position;
		this._conferenceType = conferenceType;
	}
}
